"use client";
import AnimatedText from "@/components/AnimatedText";
import AnimatedLink from "@/components/AnimatedLink";
import AnimatedBlock from "./AnimatedBlock";
import { useEffect, useState } from "react";
import translations from "@/lib/translations";
import Squircle from "@/components/Squircle";

function SafeSvg({ svg, ...props }) {
	const [ready, setReady] = useState(false);
	useEffect(() => setReady(true), []);
	if (!ready) return null;
	return <span {...props} dangerouslySetInnerHTML={{ __html: svg }} />;
}

const LinkedInSvg = `<svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
<path d="M27.2977 21.5288C27.9081 20.8414 28.4086 20.1484 29.0356 19.56C30.9604 17.7451 33.2373 16.8321 35.8991 16.8541C37.362 16.8651 38.8029 16.9696 40.2108 17.3766C43.4335 18.306 45.3034 20.5279 46.1998 23.6736C46.8708 26.033 46.9918 28.4583 46.9973 30.8891C47.0083 36.0147 46.9808 41.1459 46.9973 46.2715C46.9973 46.75 46.8653 46.8765 46.3923 46.8765C43.7525 46.8545 41.1072 46.8545 38.4674 46.8765C37.9999 46.8765 37.9009 46.739 37.9009 46.299C37.9174 41.4209 37.9174 36.5427 37.9009 31.6591C37.9009 30.4382 37.8184 29.2117 37.4774 28.0238C36.845 25.835 35.2776 24.7186 32.9843 24.8396C29.8495 25.0046 28.2216 26.5554 27.8257 29.7397C27.7322 30.4987 27.6827 31.2631 27.6882 32.0275C27.6882 36.7737 27.6882 41.5198 27.6992 46.266C27.6992 46.739 27.5837 46.8765 27.0997 46.8765C24.4379 46.8545 21.7761 46.8545 19.1143 46.8765C18.6853 46.8765 18.5643 46.7665 18.5643 46.332C18.5753 36.9387 18.5753 27.5399 18.5643 18.1466C18.5643 17.6791 18.7183 17.5746 19.1583 17.5746C21.6826 17.5911 24.2124 17.5966 26.7367 17.5746C27.2042 17.5746 27.3197 17.7231 27.3142 18.1631C27.2867 19.285 27.3032 20.4069 27.3032 21.5343L27.2977 21.5288Z" fill="#131313"/>
<path d="M12.8223 32.2635C12.8223 36.9107 12.8168 41.5579 12.8333 46.205C12.8333 46.722 12.7068 46.876 12.1734 46.8705C9.53356 46.843 6.89375 46.8485 4.24845 46.8705C3.82498 46.8705 3.69849 46.766 3.69849 46.3315C3.70949 36.9217 3.70949 27.5119 3.69849 18.0966C3.69849 17.7061 3.79198 17.5686 4.20445 17.5686C6.88275 17.5851 9.56106 17.5906 12.2394 17.5686C12.7563 17.5686 12.8223 17.7611 12.8223 18.2066C12.8113 22.8922 12.8168 27.5779 12.8168 32.2635H12.8223Z"/>
<path d="M13.5483 8.24662C13.5483 11.1669 11.1834 13.5427 8.26866 13.5427C5.39786 13.5482 3.01104 11.1669 3.00004 8.29611C2.98904 5.38683 5.37587 3 8.28515 3C11.1724 3 13.5483 5.37033 13.5538 8.24662H13.5483Z"/>
</svg>`;

const QuoteSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 38"><path d="M38.33,36.17c6.41,0,11.61-5.2,11.61-11.61,0-4.59-2.7-8.46-6.56-10.33-1.08-.52-.98-1.04-.59-1.79,1.36-2.65,2.65-5.34,3.21-7.03,1.59-4.81-3.98-6.5-7.66-3.8-4.02,2.94-11.61,11.74-11.61,22.95,0,6.41,5.2,11.61,11.61,11.61Z"/><path d="M11.61,36.17c6.41,0,11.61-5.2,11.61-11.61,0-4.59-2.7-8.46-6.56-10.33-1.08-.52-.98-1.04-.59-1.79,1.36-2.65,2.65-5.34,3.21-7.03,1.59-4.81-3.98-6.5-7.66-3.8C7.59,4.56,0,13.35,0,24.56c0,6.41,5.2,11.61,11.61,11.61Z"/></svg>`;

export default function TestimonialCard({ testimonial, lang, hideProjectLink = false }) {
	if (!testimonial) return null;

	return (
		<article className="testimonial-card padding-box">
			<Squircle size="card" />
			<div className="testimonial-content">
				<AnimatedBlock className="icon quote">
					<SafeSvg svg={QuoteSvg} />
				</AnimatedBlock>
				<AnimatedText variant="fade">
					<div className="testimonial-text">
						{testimonial.content?.map((block, blockIndex) => {
							if (block._type === "block") {
								return (
									<p key={blockIndex}>
										{block.children?.map((span, spanIndex) => {
											let text = span.text;
											if (span.marks?.includes("strong")) {
												text = <strong key={spanIndex}>{text}</strong>;
											}
											if (span.marks?.includes("em")) {
												text = <em key={spanIndex}>{text}</em>;
											}
											return text;
										})}
									</p>
								);
							}
							return null;
						})}
					</div>
				</AnimatedText>
				<AnimatedBlock className="icon quote end">
					<SafeSvg svg={QuoteSvg} />
				</AnimatedBlock>
			</div>
			<div className="testimonial-meta">
				<div className="testimonial-author">
					{testimonial.avatar?.asset?.url && (
						<AnimatedBlock className="testimonial-avatar">
							<img src={testimonial.avatar.asset.url} alt={`${testimonial.firstName} ${testimonial.lastName}`} />
						</AnimatedBlock>
					)}
					<div className="testimonial-info">
						<div className="testimonial-full-name">
							<AnimatedText variant="fade" className="testimonial-name">
								{testimonial.firstName} {testimonial.lastName}
							</AnimatedText>
							<AnimatedBlock className="icon linkedin-link">
								{testimonial.linkedinUrl && (
									<a href={testimonial.linkedinUrl} target="_blank" rel="noopener noreferrer">
										<SafeSvg svg={LinkedInSvg} />
									</a>
								)}
							</AnimatedBlock>
						</div>
						<AnimatedText variant="fade" className="testimonial-position">
							{testimonial.position} - {testimonial.clientName}
						</AnimatedText>
					</div>
				</div>
				{!hideProjectLink && (
					<div className="testimonial-actions">
						{testimonial.projectSlug && (
							<div className="testimonial-project-link">
								<AnimatedText variant="fade">
									<AnimatedLink href={`/${lang}/work/${testimonial.projectSlug}`}>{translations.home.testimoniallink[lang]}</AnimatedLink>
								</AnimatedText>
							</div>
						)}
					</div>
				)}
			</div>
		</article>
	);
}
